import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PLATFORMS, type Platform } from '@/lib/db';
import { AlertCTA } from './AlertCTA';

interface ScreenshotFrameProps {
  chrome: Platform;
  title?: string;
  label?: string;
  children: ReactNode;
  className?: string;
}

function MacChrome({ title }: { title: string | undefined }) {
  return (
    <div className="flex items-center gap-2 border-b border-border bg-muted/60 px-3 py-2">
      <span className="flex gap-1.5" aria-hidden="true">
        <span className="h-3 w-3 rounded-full bg-[#ff5f57]" />
        <span className="h-3 w-3 rounded-full bg-[#febc2e]" />
        <span className="h-3 w-3 rounded-full bg-[#28c840]" />
      </span>
      {title ? (
        <span className="ms-2 truncate text-xs text-muted-foreground">{title}</span>
      ) : null}
    </div>
  );
}

function WindowsChrome({ title }: { title: string | undefined }) {
  return (
    <div className="flex items-center justify-between border-b border-border bg-muted/60 px-3 py-2">
      {title ? (
        <span className="truncate text-xs text-muted-foreground">{title}</span>
      ) : (
        <span aria-hidden="true" />
      )}
      <span className="flex items-center gap-3 text-muted-foreground" aria-hidden="true">
        <span className="inline-block h-px w-3 bg-current" />
        <span className="inline-block h-3 w-3 border border-current" />
        <span className="font-mono text-xs leading-none">×</span>
      </span>
    </div>
  );
}

const DARK_SURFACE = 'oklch(0.10 0 0)';
const DARK_FG = 'oklch(0.985 0 0)';
const DARK_SUBTLE = 'oklch(0.556 0 0)';

export function AlertOverlayMock() {
  return (
    <div
      className="flex aspect-video flex-col items-center justify-center gap-8"
      style={{ background: DARK_SURFACE }}
    >
      <div className="flex flex-col items-center gap-2 text-center">
        <p
          className="text-xs font-semibold uppercase tracking-widest"
          style={{ color: DARK_SUBTLE }}
        >
          Upcoming meeting
        </p>
        <p className="text-2xl font-bold" style={{ color: DARK_FG }}>
          Weekly Team Standup
        </p>
        <p className="text-sm" style={{ color: DARK_SUBTLE }}>
          Google Meet · 10:00 AM
        </p>
      </div>

      <div className="flex flex-col items-center gap-1">
        <span
          className="font-mono text-5xl font-bold tabular-nums"
          style={{ color: DARK_FG }}
        >
          2:00
        </span>
        <span className="text-xs" style={{ color: DARK_SUBTLE }}>
          minutes remaining
        </span>
      </div>

      <AlertCTA label="Join meeting" href="#" size="lg" />
    </div>
  );
}

export function ScreenshotFrame({
  chrome,
  title,
  label,
  children,
  className,
}: ScreenshotFrameProps) {
  const { t } = useTranslation('common');
  const ariaLabel = label ?? t('screenshot.label');

  return (
    <figure
      aria-label={ariaLabel}
      className={[
        'overflow-hidden rounded-xl border border-border bg-card shadow-elevated',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {chrome === PLATFORMS.macos ? (
        <MacChrome title={title} />
      ) : (
        <WindowsChrome title={title} />
      )}
      <div className="bg-background">{children}</div>
    </figure>
  );
}
